import { IRequestContext } from '@msdyn365-commerce/core-internal';
import { Address, CartLine, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import { useEffect, useRef } from 'react';
import { useDatalayerService } from './useDatalayerService';

interface UseCheckoutDatalayerParams {
    request: IRequestContext;
    telemetry: ITelemetry;
    isPaymentVerificationRedirection: boolean | undefined;
    cartLines: CartLine[] | undefined;
    products: SimpleProduct[] | undefined;
    totalAmount: number | undefined;
    cartId: string | undefined;
    guestEmail: string | undefined;
    shippingAddress: Address | undefined;
    billingAddress: Address | undefined;
}

export const useCheckoutDatalayer = ({
    request,
    telemetry,
    isPaymentVerificationRedirection,
    cartLines,
    products,
    totalAmount,
    cartId,
    guestEmail,
    shippingAddress,
    billingAddress
}: UseCheckoutDatalayerParams): void => {
    const datalayerService = useDatalayerService({ request, telemetry });
    const hasFiredBeginCheckoutRef = useRef(false);
    const hasFiredContactInfoRef = useRef(false);
    const hasFiredShippingInfoRef = useRef(false);
    const hasFiredDeliveryInfoRef = useRef(false);
    const hasFiredBillingInfoRef = useRef(false);

    const resolvedCartId = cartId ?? '';
    const hasDeliveryMode = cartLines?.length ? cartLines.every(line => !!line.DeliveryMode) : false;

    useEffect(() => {
        if (typeof window === 'undefined' || request.user.isAuthenticated) {
            return;
        }
        datalayerService.saveGuestCheckoutIdentity(guestEmail, shippingAddress, billingAddress);
    }, [request.user.isAuthenticated, guestEmail, shippingAddress, billingAddress, datalayerService]);

    useEffect(() => {
        if (hasFiredBeginCheckoutRef.current || isPaymentVerificationRedirection || !cartLines?.length || !products?.length) {
            return;
        }
        hasFiredBeginCheckoutRef.current = true;
        datalayerService.pushBeginCheckout(cartLines, products, totalAmount);
    }, [isPaymentVerificationRedirection, cartLines, products, totalAmount, datalayerService]);

    useEffect(() => {
        if (hasFiredContactInfoRef.current || isPaymentVerificationRedirection || !guestEmail || !cartLines?.length || !products?.length) {
            return;
        }
        if (resolvedCartId && datalayerService.hasCheckoutEventFired(resolvedCartId, 'add_contact_info')) {
            hasFiredContactInfoRef.current = true;
            return;
        }
        hasFiredContactInfoRef.current = true;
        if (resolvedCartId) {
            datalayerService.markCheckoutEventFired(resolvedCartId, 'add_contact_info');
        }
        datalayerService.pushCheckoutStepIdentity(guestEmail, undefined, undefined);
        datalayerService.pushCheckoutStep('add_contact_info', cartLines, products, totalAmount);
    }, [isPaymentVerificationRedirection, guestEmail, cartLines, products, totalAmount, resolvedCartId, datalayerService]);

    useEffect(() => {
        if (
            hasFiredShippingInfoRef.current ||
            isPaymentVerificationRedirection ||
            !shippingAddress ||
            !cartLines?.length ||
            !products?.length
        ) {
            return;
        }
        if (resolvedCartId && datalayerService.hasCheckoutEventFired(resolvedCartId, 'add_shipping_info')) {
            hasFiredShippingInfoRef.current = true;
            return;
        }
        hasFiredShippingInfoRef.current = true;
        if (resolvedCartId) {
            datalayerService.markCheckoutEventFired(resolvedCartId, 'add_shipping_info');
        }
        datalayerService.pushCheckoutStepIdentity(guestEmail, shippingAddress, billingAddress);
        datalayerService.pushCheckoutStep('add_shipping_info', cartLines, products, totalAmount);
    }, [
        isPaymentVerificationRedirection,
        shippingAddress,
        cartLines,
        products,
        totalAmount,
        resolvedCartId,
        guestEmail,
        billingAddress,
        datalayerService
    ]);

    useEffect(() => {
        if (
            hasFiredBillingInfoRef.current ||
            isPaymentVerificationRedirection ||
            !billingAddress ||
            !cartLines?.length ||
            !products?.length
        ) {
            return;
        }
        if (resolvedCartId && datalayerService.hasCheckoutEventFired(resolvedCartId, 'add_billing_info')) {
            hasFiredBillingInfoRef.current = true;
            return;
        }
        hasFiredBillingInfoRef.current = true;
        if (resolvedCartId) {
            datalayerService.markCheckoutEventFired(resolvedCartId, 'add_billing_info');
        }
        const isBillingIdenticalToShipping =
            billingAddress?.Name === shippingAddress?.Name &&
            billingAddress?.Street === shippingAddress?.Street &&
            billingAddress?.ZipCode === shippingAddress?.ZipCode &&
            billingAddress?.City === shippingAddress?.City &&
            billingAddress?.Phone === shippingAddress?.Phone;
        if (!isBillingIdenticalToShipping) {
            datalayerService.pushCheckoutStepIdentity(guestEmail, shippingAddress, billingAddress);
        }
        datalayerService.pushCheckoutStep('add_billing_info', cartLines, products, totalAmount);
    }, [
        isPaymentVerificationRedirection,
        billingAddress,
        cartLines,
        products,
        totalAmount,
        resolvedCartId,
        guestEmail,
        shippingAddress,
        datalayerService
    ]);

    useEffect(() => {
        if (
            hasFiredDeliveryInfoRef.current ||
            isPaymentVerificationRedirection ||
            !hasDeliveryMode ||
            !cartLines?.length ||
            !products?.length
        ) {
            return;
        }
        if (resolvedCartId && datalayerService.hasCheckoutEventFired(resolvedCartId, 'add_delivery_info')) {
            hasFiredDeliveryInfoRef.current = true;
            return;
        }
        hasFiredDeliveryInfoRef.current = true;
        if (resolvedCartId) {
            datalayerService.markCheckoutEventFired(resolvedCartId, 'add_delivery_info');
        }
        datalayerService.pushCheckoutStepIdentity(guestEmail, shippingAddress, billingAddress);
        datalayerService.pushCheckoutStep('add_delivery_info', cartLines, products, totalAmount);
    }, [
        isPaymentVerificationRedirection,
        hasDeliveryMode,
        cartLines,
        products,
        totalAmount,
        resolvedCartId,
        guestEmail,
        shippingAddress,
        billingAddress,
        datalayerService
    ]);
};
